import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {ButtonComponent, LabelComponent, LocalizedDatetimePipe, TooltipDirective} from '@icz/angular-essentials';
import {
  ColumnTemplateDirective,
  FilterType,
  RowDisableFn,
  TABLE_HEADER_EL_ID_PREFIX,
  TableColumnsData,
  TableComponent,
  TableReservedColumns
} from '@icz/angular-table';
import {CrossReferenceType} from '|api/commons';
import {CrossReferenceWithDetailDto, DocumentDto, FileDto} from '|api/document';
import {CrossReferenceObjectsDatasource} from './cross-reference-objects.datasource';
import {
  CrossReferenceSearchService,
  entityTypeAsDocumentOrFile,
  EntityWithRefNumber,
  FileDetailCountType,
  FileDetailService,
  FindFunctionalPositionByIdPipe,
  ObjectClassIconComponent,
  RefNumberLinkComponent,
} from '|shared';
import {
  CrossReferenceToolbarButtonsComponent
} from './cross-reference-toolbar-buttons/cross-reference-toolbar-buttons.component';
import {AsyncPipe} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FindInListPipe} from '@icz/angular-form-elements';
import {TranslatePipe} from '@ngx-translate/core';
import {map} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {enumToOptions} from '@icz/angular-application-core';

enum CrossReferenceColumnId {
  OBJECT_CLASS = 'objectClass',
  REF_NUMBER = 'refNumber',
  SUBJECT = 'subject',
  NAME = 'name',
  TYPE = 'type',
  ROLE = 'role',
  OWNER_FUNCTIONAL_POSITION_ID = 'ownerFunctionalPositionId',
  CREATED_AT = 'createdAt'
}

@Component({
  selector: 'icz-cross-reference-table',
  templateUrl: './cross-reference-table.component.html',
  styleUrls: ['./cross-reference-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableComponent, LabelComponent, CrossReferenceToolbarButtonsComponent, ColumnTemplateDirective, ObjectClassIconComponent, RefNumberLinkComponent, TooltipDirective, AsyncPipe, LocalizedDatetimePipe, FindFunctionalPositionByIdPipe, ButtonComponent, FindInListPipe, TranslatePipe]
})
export class CrossReferenceTableComponent implements OnChanges, OnInit {

  private crossReferenceSearchService = inject(CrossReferenceSearchService);
  private fileDetailService = inject(FileDetailService, {optional: true});
  private destroyRef = inject(DestroyRef);

  @Input({required: true})
  esslEntity!: DocumentDto | FileDto;

  @Output()
  crossReferenceCountChanged = new EventEmitter<void>();

  @HostListener('window:resize')
  onResize() {
    if (!this.expandedRow) return;
    this.setColumnsDisplayedForExpanded();
  }

  selectedRows: CrossReferenceWithDetailDto[] = [];

  dataSource!: CrossReferenceObjectsDatasource;
  readonly CrossReferenceColumnId = CrossReferenceColumnId;

  rowSelectionDisabler: RowDisableFn = row => {
    return {
      disable: !(row.valid && (row.type === CrossReferenceType.WEAK_BY_USER || row.type === CrossReferenceType.STRONG)),
    };
  };

  crossReferenceTypeOptions = enumToOptions('crossReferenceType', CrossReferenceType);

  columnsData = new TableColumnsData<CrossReferenceColumnId>([
    {id: TableReservedColumns.SELECTION, label: 'Výběr', filterType: FilterType.NONE},
    {id: CrossReferenceColumnId.OBJECT_CLASS, label: 'Druh', filterType: FilterType.NONE, fixedWidth: 40,
      disableSort: true, //todo(mh) remove when ESSL-7936 is fixed on BE
    },
    {id: CrossReferenceColumnId.REF_NUMBER, label: 'ČJ/SZ', filterType: FilterType.TEXT,
      toggleable: false, disableSort: true, //todo(mh) remove when ESSL-7936 is fixed on BE
    },
    {id: CrossReferenceColumnId.SUBJECT, label: 'Věc', filterType: FilterType.TEXT,
      disableSort: true, //todo(mh) remove when ESSL-7936 is fixed on BE
    },
    {id: CrossReferenceColumnId.NAME, label: 'Odkaz', filterType: FilterType.TEXT},
    {id: CrossReferenceColumnId.TYPE, label: 'Typ', filterType: FilterType.ENUM, list: this.crossReferenceTypeOptions, displayed: false, allowTranslation: true},
    {id: CrossReferenceColumnId.ROLE, label: 'Role', filterType: FilterType.NONE, disableSort: true},
    {id: CrossReferenceColumnId.OWNER_FUNCTIONAL_POSITION_ID, label: 'Zpracovatel', filterType: FilterType.NONE, disableSort: true},
    {id: CrossReferenceColumnId.CREATED_AT, label: 'Datum založení', filterType: FilterType.DATE, displayed: false},
  ]);

  private get entityType() {
    return entityTypeAsDocumentOrFile(this.esslEntity.entityType!)!;
  }

  ngOnInit() {
    if (this.fileDetailService) {
      this.fileDetailService.counts.countsChanged$.pipe(takeUntilDestroyed(this.destroyRef), map(counts => counts[FileDetailCountType.RELATED]), distinctUntilChanged()).subscribe(_ => {
        this.dataSource?.reload(true);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges<this>) {
    if (changes.esslEntity && this.esslEntity) {
      this.setDataSource();
    }
  }

  expandedRow: Nullable<CrossReferenceWithDetailDto>;
  columnsDisplayed: {id: string; width: number}[] = [];

  thisEntityRoleInReference(row: CrossReferenceWithDetailDto): string {
    const unknownRoleLabel = 'Nepodařilo se určit roli';
    if (row.entityReferringFromId === this.esslEntity.id && row.entityReferringFromType === entityTypeAsDocumentOrFile(this.esslEntity.entityType!)) {
      return row.roleReferringTo;
    }
    else if (row.entityReferringToId === this.esslEntity.id && row.entityReferringToType === entityTypeAsDocumentOrFile(this.esslEntity.entityType!)) {
      return row.roleReferringFrom;
    }
    else {
      return unknownRoleLabel;
    }
  }

  setColumnsDisplayedForExpanded() {
    this.columnsDisplayed = [];
    const thElems = document.querySelectorAll('icz-table-surface th');
    thElems.forEach(th => {
      const idPrefixLength = TABLE_HEADER_EL_ID_PREFIX.length;
      this.columnsDisplayed.push({id: th.id.substring(idPrefixLength), width: th.clientWidth});
    });
  }

  toggleExpandedRow(row: CrossReferenceWithDetailDto, event: Event) {
    event.stopPropagation();
    this.setColumnsDisplayedForExpanded();
    this.expandedRow = this.expandedRow === row ? null : row;
  }

  setDataSource() {
    if (this.esslEntity && isNil(this.dataSource)) {
      this.dataSource = new CrossReferenceObjectsDatasource(this.crossReferenceSearchService, this.entityType, this.esslEntity.id);
    }
  }

  isCrossDirection(entity: CrossReferenceWithDetailDto) {
    return entity.entityReferringFromId === this.esslEntity.id && entity.entityReferringFromType === this.entityType;
  }

  getEntityLink(entity: CrossReferenceWithDetailDto): EntityWithRefNumber {
    const entityId = this.isCrossDirection(entity) ? entity.entityReferringToId : entity.entityReferringFromId;
    return {
      refNumber: entity.refNumber,
      entityType: entity.entityReferringToType,
      objectClass: entity.objectClass,
      id: entityId
    } as unknown as EntityWithRefNumber;
  }

  onOperationCompleted() {
    this.dataSource.reload(true);
    this.crossReferenceCountChanged.emit();
  }

}
